import { z } from 'zod'
import { expandSeries, type RecurringSeries } from './recurrence.ts'

/**
 * Meetings and attendance — the pure half.
 *
 * Two jobs live here because both are needed on the server *and* on a phone
 * that has been offline since Tuesday: turning a schedule plus a handful of
 * stored rows into the timeline a leader sees, and turning attendance records
 * into the numbers a pastor asks about.
 */

export const ATTENDANCE_STATUSES = ['present', 'absent', 'excused'] as const
export type AttendanceStatus = (typeof ATTENDANCE_STATUSES)[number]

export const MEETING_STATUSES = ['scheduled', 'cancelled'] as const
export type MeetingStatus = (typeof MEETING_STATUSES)[number]

/** A meeting the database knows about, because something happened to it. */
export interface StoredMeeting {
  occurrenceDate: string
  startsAt: Date
  endsAt: Date
  status: MeetingStatus
  title: string | null
  location: string | null
  note: string | null
  headcount: number | null
  attendanceTakenAt: Date | null
  /** Counts by status, when attendance was taken by name. */
  present: number
  absent: number
  excused: number
}

export interface MeetingView {
  /** `(groupId, occurrenceDate)` is the identity; the row id may not exist. */
  occurrenceDate: string
  startsAt: Date
  endsAt: Date
  status: MeetingStatus
  title: string | null
  location: string | null
  note: string | null
  headcount: number | null
  attendanceTakenAt: Date | null
  present: number
  absent: number
  excused: number
  /**
   * `series` — implied by the rule and not yet written down.
   * `stored` — a row exists: attendance taken, moved, cancelled or ad-hoc.
   * `ad-hoc` — a stored meeting on a date the rule never produced.
   */
  source: 'series' | 'stored' | 'ad-hoc'
  attendanceTaken: boolean
}

export interface MergeOptions {
  from?: Date
  to?: Date
  /** Cap on expanded occurrences, so an open-ended rule cannot run away. */
  limit?: number
}

/**
 * The timeline: what the rule says, overlaid with what actually happened.
 *
 * A stored row always wins — it records a meeting that was moved, cancelled or
 * held at all — and a stored row on a date the rule never produced is kept
 * rather than dropped, because "we met on the bank holiday after all" is
 * exactly the meeting somebody wants to see.
 */
export function mergeMeetings(
  schedule: RecurringSeries | null,
  stored: readonly StoredMeeting[],
  options: MergeOptions = {},
): MeetingView[] {
  const byDate = new Map<string, StoredMeeting>()
  for (const meeting of stored) byDate.set(meeting.occurrenceDate, meeting)

  const views: MeetingView[] = []
  const seen = new Set<string>()

  if (schedule) {
    for (const occurrence of expandSeries(schedule, options)) {
      const date = occurrence.localStart.slice(0, 10)
      seen.add(date)
      const row = byDate.get(date)
      views.push(
        row
          ? { ...toView(row), source: 'stored' }
          : {
              occurrenceDate: date,
              startsAt: occurrence.startsAt,
              endsAt: occurrence.endsAt,
              status: 'scheduled',
              title: null,
              location: null,
              note: null,
              headcount: null,
              attendanceTakenAt: null,
              present: 0,
              absent: 0,
              excused: 0,
              source: 'series',
              attendanceTaken: false,
            },
      )
    }
  }

  for (const row of stored) {
    if (seen.has(row.occurrenceDate)) continue
    if (options.from && row.endsAt < options.from) continue
    if (options.to && row.startsAt > options.to) continue
    views.push({ ...toView(row), source: schedule ? 'ad-hoc' : 'stored' })
  }

  return views.sort((a, b) => a.startsAt.getTime() - b.startsAt.getTime())
}

function toView(row: StoredMeeting): Omit<MeetingView, 'source'> {
  return {
    occurrenceDate: row.occurrenceDate,
    startsAt: row.startsAt,
    endsAt: row.endsAt,
    status: row.status,
    title: row.title,
    location: row.location,
    note: row.note,
    headcount: row.headcount,
    attendanceTakenAt: row.attendanceTakenAt,
    present: row.present,
    absent: row.absent,
    excused: row.excused,
    attendanceTaken: row.attendanceTakenAt !== null,
  }
}

/**
 * Where a series occurrence falls, for a date the client names.
 *
 * The attendance endpoint is addressed by local date, so the server has to
 * resolve that date back to real instants — including for a meeting nobody has
 * written a row for yet.
 */
export function occurrenceOn(
  schedule: RecurringSeries | null,
  date: string,
): { startsAt: Date; endsAt: Date } | null {
  if (!schedule) return null
  const anchor = Date.parse(`${date}T00:00:00Z`)
  if (Number.isNaN(anchor)) return null

  // Two days either side: a local date and its UTC instant can be a day apart
  // in either direction, and expanding a handful of occurrences is cheap.
  const day = 24 * 60 * 60 * 1000
  const found = expandSeries(schedule, {
    from: new Date(anchor - 2 * day),
    to: new Date(anchor + 2 * day),
    limit: 10,
  }).find((occurrence) => occurrence.localStart.slice(0, 10) === date)

  return found ? { startsAt: found.startsAt, endsAt: found.endsAt } : null
}

// ---- reporting -------------------------------------------------------------

export interface AttendanceSummary {
  /** Meetings in the window that were not cancelled. */
  meetingsHeld: number
  /** …of those, the ones somebody actually recorded. */
  meetingsRecorded: number
  totalPresent: number
  /** Mean present per recorded meeting, headcount-only meetings included. */
  averageAttendance: number
  /**
   * present / (present + absent), across meetings recorded by name.
   *
   * Excused absences are left out of the denominator on purpose: a group whose
   * members say when they cannot come would otherwise score worse than one
   * whose members say nothing, which would teach leaders to stop asking.
   *
   * null when nothing was recorded by name — an unknown rate is not 0%.
   */
  attendanceRate: number | null
}

export function summarizeAttendance(meetings: readonly MeetingView[]): AttendanceSummary {
  const held = meetings.filter((meeting) => meeting.status !== 'cancelled')
  const recorded = held.filter((meeting) => meeting.attendanceTaken)

  let present = 0
  let absent = 0
  let counted = 0

  for (const meeting of recorded) {
    // A headcount stands in for names when that is all the group gives.
    const attended = meeting.present > 0 ? meeting.present : (meeting.headcount ?? 0)
    counted += attended
    present += meeting.present
    absent += meeting.absent
  }

  const denominator = present + absent
  return {
    meetingsHeld: held.length,
    meetingsRecorded: recorded.length,
    totalPresent: counted,
    averageAttendance: recorded.length === 0 ? 0 : round1(counted / recorded.length),
    attendanceRate: denominator === 0 ? null : round1((present / denominator) * 100),
  }
}

const round1 = (value: number) => Math.round(value * 10) / 10

export interface MemberHistory {
  personId: string
  /** Local dates on which this person was recorded present. */
  presentOn: readonly string[]
}

export interface MissingMember {
  personId: string
  /** Meetings held since they were last present. */
  missedMeetings: number
  lastPresentOn: string | null
}

/**
 * Who has quietly stopped coming.
 *
 * Counted in *meetings*, not days: a fortnightly group where someone has missed
 * three is a longer silence than a weekly one, and the pastor asking the
 * question means "how many times have we met without them".
 */
export function findMissing(
  members: readonly MemberHistory[],
  meetings: readonly MeetingView[],
  threshold: number,
): MissingMember[] {
  // Only meetings that happened *and* were recorded can prove an absence. An
  // unrecorded meeting is not evidence that anybody missed it.
  const dates = meetings
    .filter((meeting) => meeting.status !== 'cancelled' && meeting.attendanceTaken)
    .map((meeting) => meeting.occurrenceDate)
    .sort()

  const missing: MissingMember[] = []
  for (const member of members) {
    const present = new Set(member.presentOn)
    let missed = 0
    for (let i = dates.length - 1; i >= 0; i -= 1) {
      const date = dates[i]
      if (date !== undefined && present.has(date)) break
      missed += 1
    }
    if (missed >= threshold) {
      const lastPresent = [...member.presentOn].sort().at(-1) ?? null
      missing.push({
        personId: member.personId,
        missedMeetings: missed,
        lastPresentOn: lastPresent,
      })
    }
  }

  return missing.sort((a, b) => b.missedMeetings - a.missedMeetings)
}

// ---- schemas ---------------------------------------------------------------

/**
 * A calendar date the client names. Exported because more than one module
 * takes one from a URL: a well-formed date that does not exist (February
 * 30th) reaches Postgres as a date literal and errors there, which is a 500
 * where a 422 belongs.
 */
export const localDateSchema = z
  .string()
  .regex(/^\d{4}-\d{2}-\d{2}$/, 'Use YYYY-MM-DD')
  // A well-formed date that does not exist: February 30th parses as Invalid
  // Date here and rolls over to March in other constructors, so the check both
  // guards NaN and round-trips. Throwing inside a refine would surface as a
  // 500 instead of a 422.
  .refine((value) => {
    const parsed = new Date(`${value}T00:00:00Z`)
    return !Number.isNaN(parsed.getTime()) && parsed.toISOString().slice(0, 10) === value
  }, 'That date does not exist')

export const attendanceEntrySchema = z
  .object({
    /** The device's id for this entry, so a replayed queue is not a duplicate. */
    clientId: z.uuid().optional(),
    personId: z.uuid().optional(),
    guestName: z.string().trim().min(1).max(120).optional(),
    status: z.enum(ATTENDANCE_STATUSES),
    note: z.string().trim().max(500).nullish(),
  })
  .refine((entry) => entry.personId !== undefined || entry.guestName !== undefined, {
    message: 'An entry names a person or a guest',
  })
export type AttendanceEntry = z.infer<typeof attendanceEntrySchema>

export const attendanceSubmissionSchema = z.object({
  occurrenceDate: localDateSchema,
  /**
   * Idempotency key for the whole submission. The offline queue replays a
   * submission until the server acknowledges it; the second arrival must be a
   * no-op, not a second set of records.
   */
  submissionId: z.uuid(),
  entries: z.array(attendanceEntrySchema).max(500).default([]),
  headcount: z.number().int().min(0).max(100_000).nullish(),
  note: z.string().trim().max(1000).nullish(),
  /** When the leader actually took it, which is not when it reached us. */
  takenAt: z.iso.datetime().optional(),
})
export type AttendanceSubmission = z.infer<typeof attendanceSubmissionSchema>

export const meetingPatchSchema = z.object({
  status: z.enum(MEETING_STATUSES).optional(),
  title: z.string().trim().max(120).nullish(),
  location: z.string().trim().max(200).nullish(),
  note: z.string().trim().max(1000).nullish(),
  /** Moving a single meeting without touching the group's rule. */
  startsAt: z.iso.datetime().optional(),
  endsAt: z.iso.datetime().optional(),
})
export type MeetingPatch = z.infer<typeof meetingPatchSchema>
